<template>
  <ContentWrap>
    <el-alert
      type="info"
      :closable="false"
      show-icon
      class="mb-12px"
      title="本店租赁商品将出现在 C 端租赁频道。请完善封面、详情轮播、详情长图、租金与押金后再上架。"
    />
    <el-form :inline="true" :model="queryParams" class="-mb-15px">
      <el-form-item label="商品名称">
        <el-input v-model="queryParams.productName" clearable class="!w-200px" @keyup.enter="handleQuery" />
      </el-form-item>
      <el-form-item label="状态">
        <el-select v-model="queryParams.status" clearable placeholder="全部" class="!w-120px">
          <el-option label="上架" :value="1" />
          <el-option label="下架" :value="0" />
        </el-select>
      </el-form-item>
      <el-form-item>
        <el-button @click="handleQuery"><Icon icon="ep:search" />搜索</el-button>
        <el-button type="primary" plain @click="openForm('create')"><Icon icon="ep:plus" />发布商品</el-button>
      </el-form-item>
    </el-form>
  </ContentWrap>
  <ContentWrap>
    <el-table v-loading="loading" :data="list">
      <el-table-column label="编号" prop="id" width="80" />
      <el-table-column label="封面" width="90">
        <template #default="s">
          <el-image
            v-if="s.row.coverImageUrl"
            :src="s.row.coverImageUrl"
            fit="cover"
            style="width: 56px; height: 56px; border-radius: 6px"
          />
        </template>
      </el-table-column>
      <el-table-column label="商品名称" prop="productName" min-width="180" show-overflow-tooltip />
      <el-table-column label="分类" prop="categoryName" width="110" />
      <el-table-column label="日租金" width="120">
        <template #default="s">¥{{ s.row.minRentPrice || 0 }}</template>
      </el-table-column>
      <el-table-column label="押金" width="100">
        <template #default="s">¥{{ s.row.deposit || 0 }}</template>
      </el-table-column>
      <el-table-column label="状态" width="90">
        <template #default="s">
          <el-switch
            :model-value="s.row.status"
            :active-value="1"
            :inactive-value="0"
            @change="(v) => changeStatus(s.row, Number(v))"
          />
        </template>
      </el-table-column>
      <el-table-column label="操作" width="140" fixed="right">
        <template #default="s">
          <el-button link type="primary" @click="openForm('update', s.row.id)">编辑</el-button>
          <el-button link type="danger" @click="handleDelete(s.row.id)">删除</el-button>
        </template>
      </el-table-column>
    </el-table>
    <Pagination
      v-model:page="queryParams.pageNo"
      v-model:limit="queryParams.pageSize"
      :total="total"
      @pagination="getList"
    />
  </ContentWrap>

  <Dialog v-model="formVisible" :title="formTitle" width="760px">
    <el-form ref="formRef" :model="form" :rules="rules" label-width="110px" v-loading="formLoading">
      <el-tabs v-model="formTab">
        <el-tab-pane label="基本与图片" name="basic">
          <el-form-item label="商品名称" prop="productName">
            <el-input v-model="form.productName" maxlength="200" show-word-limit />
          </el-form-item>
          <el-form-item label="副标题">
            <el-input v-model="form.productSubtitle" maxlength="300" placeholder="卖点一句话，展示在详情标题下" />
          </el-form-item>
          <el-form-item label="分类" prop="categoryId">
            <el-select v-model="form.categoryId" class="!w-full" @change="syncCategoryName">
              <el-option v-for="c in categories" :key="c.id" :label="c.name" :value="c.id" />
            </el-select>
          </el-form-item>
          <el-form-item label="封面图" prop="coverImageUrl">
            <UploadImg v-model="form.coverImageUrl" directory="rental/product/cover" height="110px" width="150px" />
            <span class="ml-12px text-gray-400 text-13px">列表/搜索卡片用；建议 750×750</span>
          </el-form-item>
          <el-form-item label="详情轮播图">
            <UploadImgs v-model="imageList" :limit="9" directory="rental/product/images" />
            <div class="text-gray-400 text-13px mt-6px">详情页顶部轮播；不填则回落封面图</div>
          </el-form-item>
          <el-form-item label="商品标签">
            <el-input v-model="form.tags" placeholder="英文逗号分隔，如：严选,九成新,顺丰包邮" />
          </el-form-item>
          <el-form-item label="商品详情图">
            <UploadImgs v-model="detailImageList" :limit="20" directory="rental/product/detail" />
            <div class="text-gray-400 text-13px mt-6px">
              C 端「商品详情」图文区长图；建议竖图宽 750，按展示顺序上传
            </div>
          </el-form-item>
        </el-tab-pane>

        <el-tab-pane label="价格与租赁" name="price">
          <el-form-item label="日租金" prop="minRentPrice">
            <el-input-number v-model="form.minRentPrice" :min="0" :precision="2" class="!w-full" />
          </el-form-item>
          <el-form-item label="市场参考价">
            <el-input-number v-model="form.marketPrice" :min="0" :precision="2" class="!w-full" />
          </el-form-item>
          <el-form-item label="押金">
            <el-input-number v-model="form.deposit" :min="0" :precision="2" class="!w-full" />
          </el-form-item>
          <el-form-item label="信用免押">
            <el-switch v-model="form.creditFree" :active-value="1" :inactive-value="0" />
          </el-form-item>
          <el-form-item label="所需芝麻分">
            <el-input-number v-model="form.creditScore" :min="0" :max="1000" class="!w-full" />
          </el-form-item>
          <el-form-item label="起租天数">
            <el-input-number v-model="form.minRentDays" :min="1" class="!w-full" />
          </el-form-item>
        </el-tab-pane>

        <el-tab-pane label="详情文案" name="detail">
          <el-form-item label="规格参数">
            <el-input
              v-model="form.specs"
              type="textarea"
              :rows="3"
              placeholder="键值用逗号或换行，如：芯片:A18,屏幕:6.9英寸（详情规格区展示）"
            />
          </el-form-item>
          <el-form-item label="商品描述">
            <el-input
              v-model="form.description"
              type="textarea"
              :rows="5"
              placeholder="详情页「商品详情」文案，展示在详情长图下方"
            />
          </el-form-item>
        </el-tab-pane>
      </el-tabs>
    </el-form>
    <template #footer>
      <el-button @click="formVisible = false">取消</el-button>
      <el-button type="primary" :loading="formLoading" @click="submitForm">保存</el-button>
    </template>
  </Dialog>
</template>

<script lang="ts" setup>
import { UploadImg, UploadImgs } from '@/components/UploadFile'
import * as Api from '@/api/merchant/rental/product'
import type { RentalProductVO } from '@/api/rental/product'

defineOptions({ name: 'MerchantRentalProduct' })
const message = useMessage()
const loading = ref(false)
const list = ref<any[]>([])
const total = ref(0)
const queryParams = reactive({
  pageNo: 1,
  pageSize: 20,
  productName: '',
  status: undefined as number | undefined
})
const categories = ref<any[]>([])
const formVisible = ref(false)
const formLoading = ref(false)
const formTitle = ref('')
const formType = ref('create')
const formTab = ref('basic')
const formRef = ref()
const imageList = ref<string[]>([])
/** 商品详情长图 → materialFiles */
const detailImageList = ref<string[]>([])

const emptyForm = (): RentalProductVO => ({
  productName: '',
  productSubtitle: '',
  categoryId: 1,
  categoryName: '',
  coverImageUrl: '',
  images: '',
  materialFiles: '',
  minRentPrice: 0,
  marketPrice: undefined,
  deposit: 0,
  creditFree: 1,
  creditScore: 600,
  minRentDays: 1,
  tags: '',
  specs: '',
  description: '',
  status: 1
})

const form = ref<RentalProductVO>(emptyForm())
const rules = {
  productName: [{ required: true, message: '请输入商品名称', trigger: 'blur' }],
  categoryId: [{ required: true, message: '请选择分类', trigger: 'change' }],
  coverImageUrl: [{ required: true, message: '请上传封面', trigger: 'change' }],
  minRentPrice: [{ required: true, message: '请填写日租金', trigger: 'blur' }]
}

function splitUrls(raw?: string) {
  if (!raw) return [] as string[]
  return String(raw)
    .split(/[,，]/)
    .map((s) => s.trim())
    .filter(Boolean)
}

/** 详情素材只保留图片 URL（兼容历史 pdf 附件混存） */
function splitImageUrls(raw?: string) {
  return splitUrls(raw).filter((u) => /\.(png|jpe?g|gif|webp|bmp)(\?|$)/i.test(u) || /\/rental\/product\//i.test(u))
}

const getList = async () => {
  loading.value = true
  try {
    const data = await Api.getMerchantRentalProductPage(queryParams)
    list.value = data.list
    total.value = data.total
  } finally {
    loading.value = false
  }
}
const handleQuery = () => {
  queryParams.pageNo = 1
  getList()
}
const syncCategoryName = (id: number) => {
  form.value.categoryName = categories.value.find((c) => c.id === id)?.name || ''
}
const openForm = async (type: string, id?: number) => {
  formType.value = type
  formTitle.value = type === 'create' ? '发布租赁商品' : '编辑租赁商品'
  formTab.value = 'basic'
  categories.value = (await Api.getMerchantRentalCategoryList()) || []
  if (type === 'create') {
    form.value = {
      ...emptyForm(),
      categoryId: categories.value[0]?.id || 1,
      categoryName: categories.value[0]?.name || ''
    }
    imageList.value = []
    detailImageList.value = []
  } else if (id) {
    form.value = await Api.getMerchantRentalProduct(id)
    imageList.value = splitUrls(form.value.images)
    detailImageList.value = splitImageUrls(form.value.materialFiles)
  }
  formVisible.value = true
}
const submitForm = async () => {
  await formRef.value?.validate()
  formLoading.value = true
  try {
    const images = imageList.value.filter(Boolean)
    // 无轮播时用封面兜底，保证详情页有主图
    form.value.images = (images.length ? images : form.value.coverImageUrl ? [form.value.coverImageUrl] : []).join(
      ','
    )
    form.value.materialFiles = detailImageList.value.filter(Boolean).join(',')
    if (formType.value === 'create') {
      await Api.createMerchantRentalProduct(form.value)
    } else {
      await Api.updateMerchantRentalProduct(form.value)
    }
    message.success('保存成功')
    formVisible.value = false
    getList()
  } finally {
    formLoading.value = false
  }
}
const changeStatus = async (row: any, status: number) => {
  await Api.updateMerchantRentalProductStatus(row.id, status)
  row.status = status
  message.success('状态已更新')
}
const handleDelete = async (id: number) => {
  await message.delConfirm()
  await Api.deleteMerchantRentalProduct(id)
  message.success('已删除')
  getList()
}
onMounted(getList)
</script>
